import React from "react";

export const XP_WEEK_CROWN_ASSETS = {
  gold: "/assets/crown-gold.png",
  silver: "/assets/crown-silver.png",
  bronze: "/assets/crown-bronze.png",
} as const;

export type XpWeekCrown = keyof typeof XP_WEEK_CROWN_ASSETS;

/** Stack online/offline (or watching) dots above the crown overlay (`z-[1]`) and avatar frame (`z-[2]`). */
export const AVATAR_PRESENCE_INDICATOR_Z_CLASS = "z-[3]";

/** Crown image covers ~96% of avatar; dark overlay fills edge-to-edge inside the border. */
const CROWN_IMAGE_RATIO = 0.96;
/** Inside an avatar frame the crown shrinks to clear the frame band and its bottom plate. */
const FRAMED_CROWN_IMAGE_RATIO = 0.7;
/** Optical nudge — crown peaks read high vs pixel bbox; drop-shadow adds weight below. */
const CROWN_VERTICAL_OFFSET = "4%";

const XP_CROWN_BORDER_IMAGE: Record<XpWeekCrown, string> = {
  gold: "linear-gradient(180deg, #F8FCC1 0%, #ECC86A 17.24%, #FDF19E 36.62%, #815D21 50.59%, #B48D48 66.03%, #4E2E03 84.27%, #845824 92.81%, #AE894A 99.54%)",
  silver:
    "linear-gradient(180deg, #FFF8E3 0%, #E6E6E6 17.24%, #FFFFFF 36.62%, #858585 50.59%, #ABABAB 66.03%, #4D4D4D 84.27%, #878787 92.81%, #B6B6B6 99.54%)",
  bronze:
    "linear-gradient(180deg, #FCECC1 0%, #EC9C6A 17.24%, #FDCF9E 36.62%, #813321 50.59%, #B46748 66.03%, #4E0B03 84.27%, #843224 92.81%, #AE5B4A 99.54%)",
};

/** @deprecated Size presets are no longer used; crown scales to avatar bounds. */
export type AvatarWithXpCrownSize = "xs" | "sm" | "md" | "lg" | "hero";

export function normalizeXpWeekCrown(value: unknown): XpWeekCrown | null {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "gold" || v === "silver" || v === "bronze") return v;
  return null;
}

const XP_WEEK_CROWN_PLACE: Record<XpWeekCrown, string> = {
  gold: "1st",
  silver: "2nd",
  bronze: "3rd",
};

/** Place label for the current-week avatar crown (last week's podium). */
export function xpWeekCrownReason(crown: XpWeekCrown | string | null | undefined): {
  place: string;
} | null {
  const normalized = normalizeXpWeekCrown(crown);
  if (!normalized) return null;
  return { place: XP_WEEK_CROWN_PLACE[normalized] };
}

function crownBorderStyle(crown: XpWeekCrown): React.CSSProperties {
  return {
    border: "1px solid",
    borderImageSource: XP_CROWN_BORDER_IMAGE[crown],
    borderImageSlice: 1,
  };
}

type AvatarWithXpCrownProps = {
  crown?: XpWeekCrown | string | null;
  /** @deprecated Crown overlay scales automatically to the wrapped avatar. */
  size?: AvatarWithXpCrownSize;
  className?: string;
  crownClassName?: string;
  /** Avatar frame artwork (transparent centre) drawn over the avatar and crown. */
  frameSrc?: string | null;
  children: React.ReactNode;
};

/** Wraps an avatar image/tile with the current-week XP podium crown overlay and optional avatar frame. */
export function AvatarWithXpCrown({
  crown,
  className = "",
  crownClassName = "",
  frameSrc = null,
  children,
}: AvatarWithXpCrownProps) {
  const normalized = normalizeXpWeekCrown(crown);
  const frame = String(frameSrc ?? "").trim();
  const crownRatio = frame ? FRAMED_CROWN_IMAGE_RATIO : CROWN_IMAGE_RATIO;

  return (
    <span
      className={`relative inline-flex shrink-0 ${className}`}
      style={normalized && !frame ? crownBorderStyle(normalized) : undefined}
    >
      {children}
      {normalized ? (
        <span
          className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center bg-[#1B062FE5]"
          aria-hidden
        >
          <img
            src={XP_WEEK_CROWN_ASSETS[normalized]}
            alt=""
            className={`block object-contain drop-shadow-[0_0_6px_rgba(0,0,0,0.45)] ${crownClassName}`}
            style={{
              width: `${crownRatio * 100}%`,
              height: `${crownRatio * 100}%`,
              transform: frame ? undefined : `translateY(${CROWN_VERTICAL_OFFSET})`,
            }}
            loading="lazy"
            decoding="async"
            aria-hidden
          />
        </span>
      ) : null}
      {frame ? (
        <img
          src={frame}
          alt=""
          className="pointer-events-none absolute inset-0 z-[2] block h-full w-full select-none"
          loading="lazy"
          decoding="async"
          draggable={false}
          aria-hidden
        />
      ) : null}
    </span>
  );
}
